import { FC, useEffect, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';

import { cn } from '@new/lib';

import { CHECKLIST, STEP_MS } from './buildPlanChecklist';

const LAST_STEP = CHECKLIST.length - 1;

type StepState = 'complete' | 'current' | 'pending';

const STEP_DOT_CLASS: Record<StepState, string> = {
  complete: 'bg-(--success-S600) text-(--neutral-white-NW1000)',
  current: 'bg-(--primary-O900)',
  pending: 'bg-(--neutral-black-NB200)',
};

const STEP_LABEL_CLASS: Record<StepState, string> = {
  complete: 'text-(--success-S700)!',
  current: 'font-semibold text-(--neutral-black-NB900)!',
  pending: 'text-(--neutral-black-NB400)!',
};

interface BuildPlanLoadingStepProps {
  planReady: boolean;
  onComplete: () => void;
}

export const BuildPlanLoadingStep: FC<BuildPlanLoadingStepProps> = ({ planReady, onComplete }) => {
  const [active, setActive] = useState(0);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const holdLastStep = active >= LAST_STEP && !planReady;

  useEffect(() => {
    if (active >= CHECKLIST.length || holdLastStep) {
      return;
    }
    const timeoutId = setTimeout(() => setActive((step) => step + 1), STEP_MS);
    return () => clearTimeout(timeoutId);
  }, [active, holdLastStep]);

  const animationDone = active >= CHECKLIST.length;

  useEffect(() => {
    if (!animationDone || !planReady || completedRef.current) {
      return;
    }
    completedRef.current = true;
    onCompleteRef.current();
  }, [animationDone, planReady]);

  const stepState = (index: number): StepState => {
    if (index < active) {
      return 'complete';
    }
    return index === active ? 'current' : 'pending';
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="mx-auto flex w-full max-w-xl flex-col items-center gap-6 py-10 sm:py-16"
    >
      <Loader2 aria-hidden className="size-12 animate-spin text-(--primary-O700)" />
      <div className="flex flex-col items-center gap-1 text-center">
        <h2 className="m-0! [font-family:var(--font-arvo)]! [font-size:var(--fs-h5)]! font-bold text-(--neutral-black-NB900)!">
          Building your plan…
        </h2>
        <p className="m-0! [font-size:var(--fs-b4)]! text-(--neutral-black-NB600)!">
          We&rsquo;re creating the best course plan for you. This won&rsquo;t take long.
        </p>
      </div>

      <ol className="m-0! flex w-full list-none flex-col gap-3 rounded-xl border border-(--neutral-black-NB200) bg-(--neutral-white-NW1000) p-4 sm:p-5">
        {CHECKLIST.map((label, index) => {
          const state = stepState(index);
          return (
            <li key={label} className="flex items-center gap-3" aria-current={state === 'current'}>
              <span
                className={cn(
                  'flex size-5 shrink-0 items-center justify-center rounded-full',
                  STEP_DOT_CLASS[state]
                )}
              >
                {state === 'complete' ? <Check aria-hidden className="size-3" /> : null}
              </span>
              <span className={cn('[font-size:var(--fs-b4)]!', STEP_LABEL_CLASS[state])}>
                {state === 'complete' ? label : `${label}…`}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
